import React from 'react';
import './Input.css';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Input label */
  label?: string;
  /** Error message */
  error?: string;
  /** Help text */
  helpText?: string;
  /** Full width input */
  fullWidth?: boolean;
  /** Density preset */
  density?: 'compact' | 'default' | 'comfortable';
  /** Icon before input */
  iconBefore?: React.ReactNode;
  /** Icon after input */
  iconAfter?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helpText,
      fullWidth = false,
      density = 'default',
      iconBefore,
      iconAfter,
      required,
      disabled,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${React.useId()}`;
    const errorId = error ? `${inputId}-error` : undefined;
    const helpTextId = helpText ? `${inputId}-help` : undefined;

    const wrapperClassNames = [
      'ui-input-wrapper',
      fullWidth && 'ui-input-wrapper--full-width',
      `ui-input-wrapper--${density}`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inputClassNames = [
      'ui-input',
      `ui-input--${density}`,
      error && 'ui-input--error',
      iconBefore && 'ui-input--with-icon-before',
      iconAfter && 'ui-input--with-icon-after',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClassNames}>
        {label && (
          <label htmlFor={inputId} className="ui-input__label">
            {label}
            {required && <span className="ui-input__required" aria-label="required"> *</span>}
          </label>
        )}
        <div className="ui-input__container">
          {iconBefore && (
            <span className="ui-input__icon-before" aria-hidden="true">
              {iconBefore}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={inputClassNames}
            disabled={disabled}
            required={required}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={[errorId, helpTextId].filter(Boolean).join(' ') || undefined}
            {...props}
          />
          {iconAfter && (
            <span className="ui-input__icon-after" aria-hidden="true">
              {iconAfter}
            </span>
          )}
        </div>
        {error && (
          <span id={errorId} className="ui-input__error" role="alert">
            {error}
          </span>
        )}
        {helpText && !error && (
          <span id={helpTextId} className="ui-input__help-text">
            {helpText}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Textarea label */
  label?: string;
  /** Error message */
  error?: string;
  /** Help text */
  helpText?: string;
  /** Full width textarea */
  fullWidth?: boolean;
  /** Density preset */
  density?: 'compact' | 'default' | 'comfortable';
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helpText,
      fullWidth = false,
      density = 'default',
      required,
      disabled,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const textareaId = id || `textarea-${React.useId()}`;
    const errorId = error ? `${textareaId}-error` : undefined;
    const helpTextId = helpText ? `${textareaId}-help` : undefined;

    const wrapperClassNames = [
      'ui-input-wrapper',
      fullWidth && 'ui-input-wrapper--full-width',
      `ui-input-wrapper--${density}`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const textareaClassNames = [
      'ui-textarea',
      `ui-textarea--${density}`,
      error && 'ui-textarea--error',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClassNames}>
        {label && (
          <label htmlFor={textareaId} className="ui-input__label">
            {label}
            {required && <span className="ui-input__required" aria-label="required"> *</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={textareaClassNames}
          disabled={disabled}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={[errorId, helpTextId].filter(Boolean).join(' ') || undefined}
          {...props}
        />
        {error && (
          <span id={errorId} className="ui-input__error" role="alert">
            {error}
          </span>
        )}
        {helpText && !error && (
          <span id={helpTextId} className="ui-input__help-text">
            {helpText}
          </span>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
